import React, { useEffect, useState } from "react";
import {
  Button,
  MenuItem,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  Popover,
  Checkbox,
  ListItemText,
  Badge,
  CircularProgress,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { FilterAltOutlined } from "@mui/icons-material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  borderColors,
  ColorsConstants,
  grayFontColor,
  inputColor,
} from "../cssUtils/constants";
import ButtonTwo from "../common/dashboard-button";
import ButtonOne from "../common/normal-button";
import typography from "../cssUtils/typography";
import {
  validatePhoneFilterValue,
  sanitizePhoneLikeSearchInput,
  PHONE_SEARCH_ERROR_MESSAGE,
} from "../utils/phoneSearch";

const MAX_CONDITIONS = 5;

// Filter configuration by field type
const filterConfig = {
  text: {
    operators: [
      "is",
      "is not",
      "contains",
      "does not contain",
      "starts with",
      "ends with",
      "is empty",
      "is not empty",
    ],
  },
  date: {
    operators: ["is", "before", "after"],
  },
  boolean: {
    operators: ["is", "is not"],
    values: ["true", "false"],
  },
  list: {
    operators: [
      "is",
      "is not",
      "contains any",
      "contains all",
      "does not contain",
      "start with",
      "end with",
      "is empty",
      "is not empty",
    ],
  },
  single_select_list: {
    operators: ["is", "is not", "is empty", "is not empty"],
  },
  phone: {
    operators: ["is"],
  },
};

/**
 * Filter Component
 *
 * @param {Array} fields - Array of filter field objects. Each object should have:
 *   - label: string (display name)
 *   - type: "text" | "date" | "boolean" | "list"
 *   - options?: string[] (required if type = "list")
 *
 * @param {Function} onApply - Callback called when user applies filters.
 *   - Receives an array of filter objects:
 *     [
 *       {
 *         field: string,          // field name (e.g., "gender")
 *         operator: string,       // selected operator (e.g., "is", "is not")
 *         value: string | Date,   // value of the condition (if applicable)
 *       },
 *       ...
 *     ]
 */
export default function Filter({
  fields,
  onApply,
  loading = false,
  initalFilters = [],
}) {
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [appliedConditionCount, setAppliedConditionCount] = useState(0);
  const [applyValidationError, setApplyValidationError] = useState(null);

  const popoverOpen = Boolean(popoverAnchor);

  const handleOpen = (event) => {
    setApplyValidationError(null);
    setPopoverAnchor(event.currentTarget);
  };
  const handleClose = () => setPopoverAnchor(null);

  const handleConditionChange = (index, key, value) => {
    const updated = [...conditions];
    // A new field gets a fresh operator/value: a leftover string or array fed
    // to the DatePicker throws (value.isValid is not a function) and blanks the page.
    updated[index] =
      key === "field"
        ? { field: value, operator: "", value: "" }
        : { ...updated[index], [key]: value };
    setConditions(updated);
  };

  const handleAddCondition = () => {
    if (conditions.length < MAX_CONDITIONS) {
      setConditions([...conditions, { field: "", operator: "", value: "" }]);
    }
  };

  const handleDeleteCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleApply = () => {
    setApplyValidationError(null);
    const cleanFilters = [];

    for (const c of conditions.filter((row) => row.field && row.operator)) {
      const fieldObj = fields.find((f) => f.label === c.field);
      let value =
        fieldObj?.type === "date" && c.value?.format
          ? c.value.format("YYYY-MM-DD")
          : c.value;

      if (
        fieldObj?.type === "phone" &&
        c.operator !== "is empty" &&
        c.operator !== "is not empty" &&
        value !== "" &&
        value != null
      ) {
        const check = validatePhoneFilterValue(String(value));
        if (!check.valid) {
          setApplyValidationError(check.error || PHONE_SEARCH_ERROR_MESSAGE);
          return;
        }
        value = check.normalizedForApi;
      }

      cleanFilters.push({
        field: c.field,
        operator: c.operator,
        value,
      });
    }

    if (onApply) onApply(cleanFilters);
    setAppliedConditionCount(cleanFilters.length);
    setPopoverAnchor(null);
  };

  const handleClear = () => {
    setConditions([]);
    setAppliedConditionCount(0);
    setApplyValidationError(null);

    if (onApply) onApply([]);

    setPopoverAnchor(null);
  };

  useEffect(() => {
    if (initalFilters.length > 0) {
      console.log("Initial Filters: ", initalFilters);

      const normalizedFilters = initalFilters.map((f) => {
        const fieldObj = fields.find((fl) => fl.label === f.field);
        if (fieldObj?.type === "date" && f.value) {
          return { ...f, value: dayjs(f.value) }; // convert string → dayjs
        }
        return f;
      });

      setConditions(normalizedFilters);
      setAppliedConditionCount(normalizedFilters.length);
    }
  }, [initalFilters]);

  const displayBooleanValue = (selectedField, value) => {
    if (selectedField?.display) {
      if (value === "true") {
        return selectedField.display[0];
      } else {
        return selectedField.display[1];
      }
    }
    return value;
  };

  return (
    <Box>
      <Badge
        badgeContent={appliedConditionCount}
        color="primary"
        invisible={appliedConditionCount === 0}
      >
        <Button
          variant="outlined"
          onClick={handleOpen}
          endIcon={
            loading ? (
              <CircularProgress size={20} />
            ) : (
              <FilterAltOutlined size={20} />
            )
          }
          sx={{
            borderRadius: "8px",
            ...typography.bodyLarge,
            height: 40,
            textTransform: "none",
            border: `1px solid ${ColorsConstants.inputFieldGrey}`,
            color: inputColor,
            "&:hover": {
              border: `1px solid ${ColorsConstants.primaryGray50}`,
              backgroundColor: "transparent",
            },
            "&.Mui-focusVisible": {
              border: `1px solid ${ColorsConstants.primaryGray50}`,
            },
          }}
        >
          Filter
        </Button>
      </Badge>

      {/* Floating filter box */}
      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, pt: 0, width: 650 }}>
          <h4>Filters</h4>

          {applyValidationError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {applyValidationError}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {conditions?.map((cond, index) => {
              const selectedField = fields.find((f) => f.label === cond.field);

              return (
                <Box
                  key={index}
                  display="flex"
                  gap={1}
                  mb={1}
                  alignItems="center"
                >
                  {/* Field selector */}
                  <FormControl
                    size="small"
                    sx={{
                      width: 200,
                      // border: 1,
                      borderColor: ColorsConstants.primaryBlue100,
                    }}
                  >
                    <InputLabel>Field</InputLabel>
                    <Select
                      value={cond.field}
                      label="Field"
                      onChange={(e) =>
                        handleConditionChange(index, "field", e.target.value)
                      }
                    >
                      {fields.map((f) => (
                        <MenuItem key={f.label} value={f.label}>
                          {f.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Operator selector */}
                  {selectedField && (
                    <FormControl size="small" sx={{ width: 150 }}>
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={cond.operator}
                        label="Operator"
                        onChange={(e) =>
                          handleConditionChange(
                            index,
                            "operator",
                            e.target.value
                          )
                        }
                      >
                        {(selectedField.operators || filterConfig[selectedField.type].operators)?.map(
                          (op) => (
                            <MenuItem key={op} value={op}>
                              {op}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  )}

                  {/* Value input */}
                  {selectedField &&
                    cond.operator !== "is empty" &&
                    cond.operator !== "is not empty" && (
                      <>
                        {selectedField.type === "boolean" ? (
                          <Select
                            size="small"
                            value={cond.value}
                            onChange={(e) =>
                              handleConditionChange(
                                index,
                                "value",
                                e.target.value
                              )
                            }
                            sx={{ width: 200 }}
                          >
                            {filterConfig.boolean.values?.map((val) => (
                              <MenuItem key={val} value={val}>
                                {displayBooleanValue(selectedField, val)}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : selectedField.type === "date" ? (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              format="DD-MM-YYYY"
                              value={cond.value || null}
                              onChange={(newValue) =>
                                handleConditionChange(index, "value", newValue)
                              }
                              slotProps={{
                                textField: { size: "small" },
                              }}
                              sx={{ width: 200 }}
                            />
                          </LocalizationProvider>
                        ) : selectedField.type === "list" &&
                          cond.operator !== "start with" &&
                          cond.operator !== "end with" ? (
                          <Select
                            multiple
                            size="small"
                            value={
                              cond.value
                                ? Array.isArray(cond.value)
                                  ? cond.value
                                  : []
                                : []
                            }
                            onChange={(e) =>
                              handleConditionChange(
                                index,
                                "value",
                                e.target.value
                              )
                            }
                            renderValue={(selected) => selected.join(", ")}
                            sx={{ width: 200 }}
                          >
                            {selectedField.values?.map((val) => (
                              <MenuItem key={val} value={val}>
                                <Checkbox
                                  checked={cond.value?.indexOf(val) > -1}
                                />
                                <ListItemText primary={val} />
                              </MenuItem>
                            ))}
                          </Select>
                        ) : selectedField.type === "single_select_list" ? (
                          <Select
                            size="small"
                            value={
                              cond.value
                                ? Array.isArray(cond.value)
                                  ? ""
                                  : cond.value
                                : ""
                            }
                            onChange={(e) =>
                              handleConditionChange(
                                index,
                                "value",
                                e.target.value
                              )
                            }
                            renderValue={(selected) => selected}
                            sx={{ width: 200 }}
                          >
                            {selectedField.values?.map((val) => (
                              <MenuItem key={val} value={val}>
                                <ListItemText primary={val} />
                              </MenuItem>
                            ))}
                          </Select>
                        ) : selectedField.type === "phone" ? (
                          <TextField
                            size="small"
                            placeholder="Phone"
                            value={
                              cond.value
                                ? Array.isArray(cond.value)
                                  ? ""
                                  : cond.value
                                : ""
                            }
                            onChange={(e) =>
                              handleConditionChange(
                                index,
                                "value",
                                sanitizePhoneLikeSearchInput(e.target.value),
                              )
                            }
                            sx={{ width: 200 }}
                          />
                        ) : (
                          <TextField
                            size="small"
                            placeholder="Value"
                            value={
                              cond.value
                                ? Array.isArray(cond.value)
                                  ? ""
                                  : cond.value
                                : ""
                            }
                            onChange={(e) =>
                              handleConditionChange(
                                index,
                                "value",
                                e.target.value
                              )
                            }
                            sx={{ width: 200 }}
                          />
                        )}
                      </>
                    )}

                  {/* Delete condition */}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteCondition(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              );
            })}
          </Box>

          {conditions.length < MAX_CONDITIONS && (
            <Button
              onClick={handleAddCondition}
              size="small"
              sx={{ color: ColorsConstants.primaryBlue100 }}
            >
              + Add Condition
            </Button>
          )}

          <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
            {conditions.length > 0 && (
              <ButtonTwo
                title={"Clear Filters"}
                size="small"
                variant="outlined"
                onClick={handleClear}
              />
            )}

            <ButtonTwo title={"Cancel"} size="small" onClick={handleClose} />

            <ButtonOne
              title={"Apply"}
              variant="contained"
              size="small"
              onClick={handleApply}
            ></ButtonOne>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
